if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.section = document.getElementById(`MainProduct-${this.sectionId}`);
        this.form = document.getElementById(this.productFormId);
        this.productForm = this.form?.closest('product-form');
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.buttonLabel = this.querySelector('[data-sticky-atc-label]');
        this.buttonPriceTarget = this.querySelector('[data-sticky-atc-button-price]');
        this.priceTarget = this.querySelector('[data-sticky-atc-price]');
        this.selectionTarget = this.querySelector('[data-sticky-atc-selection]');
        this.errorTarget = this.querySelector('[data-sticky-atc-error]');

        if (!this.section || !this.form || !this.productForm || !this.button || !this.buttonLabel) return;

        this.onStickyClick = this.onStickyClick.bind(this);
        this.onSectionChange = this.onSectionChange.bind(this);
        this.onSourceIntersection = this.onSourceIntersection.bind(this);
        this.onSourceMutation = this.onSourceMutation.bind(this);
        this.onFrequentlyBoughtTogetherMutation = this.onFrequentlyBoughtTogetherMutation.bind(this);
        this.onDocumentReady = this.onDocumentReady.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onAnimationEnd = this.onAnimationEnd.bind(this);
        this.updateRailHeight = this.updateRailHeight.bind(this);

        this.bodyPaddingBottom = getComputedStyle(document.body).paddingBottom;
        document.body.style.setProperty('--product-sticky-atc-body-padding', this.bodyPaddingBottom);

        this.button.addEventListener('click', this.onStickyClick);
        this.addEventListener('animationend', this.onAnimationEnd);
        this.section.addEventListener('change', this.onSectionChange);
        this.section.addEventListener('input', this.onSectionChange);
        this.form.addEventListener('submit', this.onSectionChange);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.sourceObserver = new MutationObserver(this.onSourceMutation);
        const sourceRoot = document.getElementById(`ProductInfo-${this.sectionId}`) || this.productForm;
        this.sourceObserver.observe(sourceRoot, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'aria-haspopup', 'class', 'disabled', 'hidden', 'value'],
          characterData: true,
          childList: true,
          subtree: true,
        });

        this.frequentlyBoughtTogetherHosts = [];
        this.frequentlyBoughtTogetherObserver = new MutationObserver(this.onFrequentlyBoughtTogetherMutation);
        this.observeFrequentlyBoughtTogetherHosts();
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
        }

        this.resizeObserver = new ResizeObserver(this.updateRailHeight);
        this.resizeObserver.observe(this);

        this.refreshSources();
        this.syncState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onStickyClick);
        this.removeEventListener('animationend', this.onAnimationEnd);
        this.section?.removeEventListener('change', this.onSectionChange);
        this.section?.removeEventListener('input', this.onSectionChange);
        this.form?.removeEventListener('submit', this.onSectionChange);
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('DOMContentLoaded', this.onDocumentReady);
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.intersectionObservers?.forEach((observer) => observer.disconnect());
        this.sourceObserver?.disconnect();
        this.frequentlyBoughtTogetherObserver?.disconnect();
        this.resizeObserver?.disconnect();
        document.body.classList.remove('product-sticky-atc-visible');
        document.body.style.removeProperty('--product-sticky-atc-height');
        document.body.style.removeProperty('--product-sticky-atc-body-padding');
      }

      refreshSources(forceObservation = false) {
        const nextForm = document.getElementById(this.productFormId);
        let formChanged = false;

        if (nextForm && nextForm !== this.form) {
          this.form.removeEventListener('submit', this.onSectionChange);
          this.form = nextForm;
          this.productForm = this.form.closest('product-form');
          this.form.addEventListener('submit', this.onSectionChange);
          formChanged = true;
        }

        const nextSourceButton = this.form.querySelector('[name="add"]');

        if (nextSourceButton !== this.sourceButton || formChanged || forceObservation) {
          this.sourceButton = nextSourceButton;
          this.cloneSourceSpinner();
        }

        this.refreshVisibilitySource(formChanged || forceObservation);

        this.priceSource = document.querySelector(`#price-${this.sectionId} .price`);
        this.errorSource = this.productForm.querySelector('.product-form__error-message-wrapper');
      }

      getFrequentlyBoughtTogetherWidget() {
        return Array.from(
          document.querySelectorAll(
            '[data-rebuy-id="212349"] .rebuy-widget.widget-type-dynamic-bundle.is-visible, ' +
              '[data-rebuy-id="274046"] .rebuy-widget.widget-type-dynamic-bundle.is-visible'
          )
        ).find((widget) => {
          const widgetContainer = widget.querySelector('.rebuy-widget-container.is-visible');

          return widgetContainer && widget.getClientRects().length > 0;
        });
      }

      observeFrequentlyBoughtTogetherHosts() {
        this.frequentlyBoughtTogetherObserver.disconnect();
        this.frequentlyBoughtTogetherHosts = Array.from(
          document.querySelectorAll('[data-rebuy-id="212349"], [data-rebuy-id="274046"]')
        );
        this.frequentlyBoughtTogetherHosts.forEach((host) => {
          this.frequentlyBoughtTogetherObserver.observe(host, {
            attributes: true,
            attributeFilter: ['class', 'hidden', 'style'],
            childList: true,
            subtree: true,
          });
        });
      }

      onDocumentReady() {
        this.observeFrequentlyBoughtTogetherHosts();
        this.refreshVisibilitySource();
        this.updateVisibility();
      }

      refreshVisibilitySource(forceObservation = false) {
        const nextVisibilitySource = this.getFrequentlyBoughtTogetherWidget() || this.sourceButton;

        if (nextVisibilitySource === this.visibilitySource && !forceObservation) return;

        this.intersectionObservers?.forEach((observer) => observer.disconnect());

        this.visibilitySource = nextVisibilitySource;

        if (!this.visibilitySource) {
          this.setVisible(false);
          return;
        }

        const viewportTopBoundary = this.getViewportTopBoundary();
        this.intersectionObservers = [
          new IntersectionObserver(this.onSourceIntersection, {
            rootMargin: '100000px 0px 0px 0px',
            threshold: 0,
          }),
          new IntersectionObserver(this.onSourceIntersection, {
            rootMargin: `-${viewportTopBoundary}px 0px 100000px 0px`,
            threshold: 0,
          }),
        ];
        this.intersectionObservers.forEach((observer) => observer.observe(this.visibilitySource));
      }

      getViewportTopBoundary() {
        const stickyHeader = document.querySelector('#shopify-section-header.shopify-section-header-sticky');

        return Math.max(0, stickyHeader?.getBoundingClientRect().bottom || 0);
      }

      cloneSourceSpinner() {
        this.spinner?.remove();
        this.spinner = null;

        const sourceSpinner = this.sourceButton?.querySelector('.loading-overlay__spinner');
        if (!sourceSpinner) return;

        this.spinner = sourceSpinner.cloneNode(true);
        this.spinner.removeAttribute('id');
        this.button.appendChild(this.spinner);
      }

      onSourceIntersection() {
        this.updateVisibility();
      }

      updateVisibility() {
        this.refreshVisibilitySource();

        if (!this.visibilitySource || this.visibilitySource.getClientRects().length === 0) {
          this.setVisible(false);
          return;
        }

        const sourceRect = this.visibilitySource.getBoundingClientRect();
        this.setVisible(sourceRect.bottom <= this.getViewportTopBoundary());
      }

      setVisible(visible) {
        if (visible) {
          if (!this.hidden && !this.classList.contains('is-leaving')) return;

          this.classList.remove('is-leaving');
          this.hidden = false;
          document.body.classList.add('product-sticky-atc-visible');
          this.updateRailHeight();
          return;
        }

        if (this.hidden || this.classList.contains('is-leaving')) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.finishHide();
          return;
        }

        this.classList.add('is-leaving');
      }

      onAnimationEnd(event) {
        if (event.target !== this || event.animationName !== 'product-sticky-atc-exit') return;

        this.finishHide();
      }

      finishHide() {
        this.hidden = true;
        this.classList.remove('is-leaving');
        document.body.classList.remove('product-sticky-atc-visible');
      }

      updateRailHeight() {
        if (this.hidden) return;

        const railHeight = Math.ceil(this.getBoundingClientRect().height);
        document.body.style.setProperty('--product-sticky-atc-height', `${railHeight}px`);
      }

      onSourceMutation(mutations) {
        const sourceWasReparented = mutations.some(
          (mutation) =>
            mutation.type === 'childList' &&
            [...mutation.addedNodes, ...mutation.removedNodes].some(
              (node) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.id === this.productFormId ||
                  node.matches?.('[name="add"]') ||
                  node.querySelector?.('[name="add"]'))
            )
        );

        this.refreshSources(sourceWasReparented);
        this.syncState();
        this.updateVisibility();
      }

      onFrequentlyBoughtTogetherMutation() {
        this.refreshVisibilitySource();
        this.updateVisibility();
      }

      onScroll() {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateVisibility();
        });
      }

      onSectionChange() {
        this.refreshSources();
        this.syncState();
      }

      syncState() {
        if (!this.sourceButton) {
          this.setVisible(false);
          return;
        }

        this.syncButton();
        this.syncPrice();
        this.syncSelection();
        this.syncError();
      }

      syncButton() {
        const sourceLabel = Array.from(this.sourceButton.children).find((child) => child.tagName === 'SPAN');
        const sourceText = sourceLabel?.textContent.trim() || this.sourceButton.textContent.trim();
        const ariaDisabled = this.sourceButton.getAttribute('aria-disabled');
        const ariaHasPopup = this.sourceButton.getAttribute('aria-haspopup');
        const ariaLabel = this.sourceButton.getAttribute('aria-label');
        const isLoading = this.sourceButton.classList.contains('loading');

        if (sourceText) this.buttonLabel.textContent = sourceText;

        this.button.disabled = this.sourceButton.disabled;
        this.button.classList.toggle('loading', isLoading);
        if (isLoading) {
          this.button.setAttribute('aria-busy', 'true');
        } else {
          this.button.removeAttribute('aria-busy');
        }

        this.copyAttribute('aria-disabled', ariaDisabled);
        this.copyAttribute('aria-haspopup', ariaHasPopup);
        this.copyAttribute('aria-label', ariaLabel);

        const sourceSpinner = this.sourceButton.querySelector('.loading-overlay__spinner');
        if (sourceSpinner && this.spinner) this.spinner.className = sourceSpinner.className;
      }

      copyAttribute(name, value) {
        if (value === null) {
          this.button.removeAttribute(name);
        } else {
          this.button.setAttribute(name, value);
        }
      }

      syncPrice() {
        if (!this.priceSource) {
          if (this.buttonPriceTarget) this.buttonPriceTarget.textContent = '';
          return;
        }

        if (this.priceTarget) {
          const price = this.priceSource.cloneNode(true);
          price.classList.remove('price--show-badge');
          price.removeAttribute('id');
          price.removeAttribute('role');
          price.querySelectorAll('.badge').forEach((badge) => badge.remove());
          price.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          this.priceTarget.replaceChildren(price);
        }

        if (this.buttonPriceTarget) {
          const activePrice = this.priceSource.classList.contains('price--on-sale')
            ? this.priceSource.querySelector('.price__sale .price-item--sale')
            : this.priceSource.querySelector('.price__regular .price-item--regular');
          const priceText = activePrice?.textContent.replace(/\s+/g, ' ').trim() || '';
          this.buttonPriceTarget.textContent = priceText ? `\u00a0- ${priceText}` : '';
        }
      }

      syncSelection() {
        if (!this.selectionTarget) return;

        const context = [this.getVariantText(), this.getSellingPlanText()].filter(Boolean).join(' · ');
        this.selectionTarget.textContent = context;
        this.selectionTarget.hidden = context.length === 0;
      }

      getVariantText() {
        const picker = this.section.querySelector(
          `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
        );
        if (!picker) return '';

        const variantId = String(this.form.elements.namedItem('id')?.value || '');
        const variantData = picker.querySelector('script[type="application/json"]');

        if (variantId && variantData) {
          try {
            const variant = JSON.parse(variantData.textContent).find((item) => String(item.id) === variantId);
            if (variant?.title && variant.title.toLowerCase() !== 'default title') return variant.title;
          } catch {
            // Fall through to the selected option labels when app markup changes the canonical JSON.
          }
        }

        const selectedValues = [];

        picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          const label = Array.from(picker.querySelectorAll('label')).find((item) => item.htmlFor === input.id);
          const value = label?.textContent.trim() || input.value;
          if (value) selectedValues.push(value);
        });

        picker.querySelectorAll('select').forEach((select) => {
          const value = select.selectedOptions[0]?.textContent.trim();
          if (value) selectedValues.push(value);
        });

        return selectedValues.filter((value) => value.toLowerCase() !== 'default title').join(' / ');
      }

      getSellingPlanText() {
        const controls = Array.from(this.form.elements).filter((control) => control.name === 'selling_plan');
        const selectedControl = controls.find((control) => {
          if (control.tagName === 'SELECT') return Boolean(control.value);
          if (control.type === 'radio') return control.checked;
          return false;
        });

        if (!selectedControl) return '';

        if (selectedControl.tagName === 'SELECT') {
          return selectedControl.selectedOptions[0]?.textContent.trim() || '';
        }

        const label = Array.from(this.form.querySelectorAll('label')).find(
          (item) => item.htmlFor === selectedControl.id
        );
        return label?.textContent.trim() || '';
      }

      syncError() {
        if (!this.errorTarget) return;

        const errorMessage = this.errorSource?.querySelector('.product-form__error-message')?.textContent.trim() || '';
        const hasVisibleError = Boolean(errorMessage && this.errorSource && !this.errorSource.hidden);

        this.errorTarget.textContent = hasVisibleError ? errorMessage : '';
        this.errorTarget.hidden = !hasVisibleError;
      }

      onStickyClick() {
        if (
          this.isForwarding ||
          !this.sourceButton ||
          this.sourceButton.disabled ||
          this.sourceButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.isForwarding = true;
        this.sourceButton.click();
        queueMicrotask(() => {
          this.isForwarding = false;
        });
      }
    }
  );
}
